import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList, Tooltip, ResponsiveContainer } from 'recharts';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import ChartCard, { ChartTableExport } from '../components/ChartCard';
import BreakDetailPanel from '../components/BreakDetailPanel';
import SpotPriceCurve from '../components/SpotPriceCurve';
import api from '../utils/api';
import { formatCurrency, formatDate, formatDuration, getDatePreset } from '../utils/formatters';
import DateRangeFilter from '../components/DateRangeFilter';
import { useAuth } from '../contexts/AuthContext';
import { scoreColor, scoreBg } from '../utils/breakScore';
import useIsMobile from '../hooks/useIsMobile';

const ResponsiveGridLayout = WidthProvider(Responsive);

const HOUR_LABELS = Array.from({ length: 24 }, (_, h) =>
  h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`
);

// Chart catalog — must stay in sync with ALLOWED_CHART_IDS in server/routes/insights.js.
// Only the two fixed-height bar charts live in the grid. Everything else — the
// peak callouts, the three tables and the heatmap — renders in flow at its
// natural height: react-grid-layout sizes an item in whole 70px rows, so a
// block whose content doesn't land on that boundary is padded out with dead
// space (the heatmap sat in a 480px row around 290px of content).
const CHART_IDS = ['by-hour', 'by-day'];

const DEFAULT_LAYOUT = [
  { i: 'by-hour', x: 0, y: 0, w: 6, h: 4 },
  { i: 'by-day',  x: 6, y: 0, w: 6, h: 4 },
];

// Reconcile a saved layout with the known chart IDs — drop unknowns (including
// the ids that no longer live in the grid), append defaults for any new charts
// added in code since the user last saved. The grid compacts vertically, so a
// legacy y from when the callouts sat above these charts floats back up to 0.
function mergeWithDefaults(saved) {
  if (!Array.isArray(saved) || saved.length === 0) return DEFAULT_LAYOUT.slice();
  const knownIds = new Set(CHART_IDS);
  const merged = saved
    .filter(it => it && typeof it.i === 'string' && knownIds.has(it.i))
    .map(it => ({ i: it.i, x: it.x, y: it.y, w: it.w, h: it.h }));
  const seen = new Set(merged.map(it => it.i));
  for (const def of DEFAULT_LAYOUT) {
    if (!seen.has(def.i)) merged.push({ ...def });
  }
  return merged;
}

function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

// Combine YYYY-MM-DD date + HH:MM scheduled_time into a display string.
function formatBreakDateTime(b) {
  if (!b?.date) return '--';
  const d = formatDate(b.date);
  const t = b.scheduled_time;
  if (!t) return d;
  const [hhStr, mmStr] = t.split(':');
  const hh = Number(hhStr);
  const mm = Number(mmStr);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return d;
  const ampm = hh < 12 ? 'AM' : 'PM';
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${d} ${h12}:${String(mm).padStart(2, '0')} ${ampm}`;
}

function HeatmapCell({ value, max, onClick, selected }) {
  const intensity = max > 0 ? value / max : 0;
  const bg = intensity === 0
    ? 'var(--bg)'
    : `rgba(34, 197, 94, ${0.1 + intensity * 0.85})`;
  const textColor = intensity > 0.5 ? 'white' : 'var(--text)';
  return (
    <td onClick={onClick} style={{
      background: bg, color: textColor,
      padding: '6px 4px', textAlign: 'center', fontSize: '11px', fontWeight: intensity > 0.3 ? 600 : 400,
      cursor: 'pointer', borderRadius: '3px', minWidth: '36px',
      outline: selected ? '2px solid var(--primary)' : 'none',
      transition: 'background 0.15s',
    }}>
      {value > 0 ? (value >= 1000 ? `$${(value / 1000).toFixed(1)}k` : `$${Math.round(value)}`) : ''}
    </td>
  );
}

// Custom tooltip — shows label, metric value, and (when present) the run count.
function LeaderboardTooltip({ active, payload, metricLabel, valueFormatter }) {
  if (!active || !payload || payload.length === 0) return null;
  const item = payload[0].payload;
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', boxShadow: 'var(--shadow)', fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{item.label}</div>
      <div>{metricLabel}: <strong>{valueFormatter(item.value)}</strong></div>
      {item.mix_count != null && <div>Runs: <strong>{item.mix_count}</strong></div>}
      {item.units_sold != null && <div>Units sold: <strong>{item.units_sold}</strong></div>}
    </div>
  );
}

const PAGE_SIZES = [10, 20, 50, 100, 'all'];

// Row count + page size for a leaderboard table. Lives in the card header
// (ChartCard's `controls` slot) rather than in a row of its own — right-aligned
// on a line by itself it left the whole width of the card empty beside it.
function TableControls({ total, pageSize, onPageSizeChange }) {
  if (!total) return null;
  const shown = pageSize === 'all' ? total : Math.min(pageSize, total);
  return (
    // One group, so the count and the page size wrap together rather than
    // splitting across two lines on a phone.
    <span className="chart-toggle" style={{ gap: 10 }}>
      <span>{pageSize === 'all' ? `${total}` : `${shown} of ${total}`}</span>
      <label>
        Show:
        <select
          value={String(pageSize)}
          onChange={e => onPageSizeChange(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
        >
          {PAGE_SIZES.map(sz => <option key={sz} value={String(sz)}>{sz === 'all' ? 'All' : sz}</option>)}
        </select>
      </label>
    </span>
  );
}

// Sortable, ranked table used for Top Compositions and Top Breaks.
function LeaderboardTable({ rows, columns, defaultSortKey, defaultSortDir = 'desc', rowKey, emptyText, pageSize, onRowClick, expandable, renderExpanded }) {
  const [sortKey, setSortKey] = useState(defaultSortKey);
  const [sortDir, setSortDir] = useState(defaultSortDir);
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const toggleExpanded = (key) => setExpandedKeys(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  function toggleSort(col) {
    if (sortKey === col.key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(col.key);
      setSortDir(col.type === 'string' ? 'asc' : 'desc');
    }
  }

  const sorted = useMemo(() => {
    const col = columns.find(c => c.key === sortKey) || columns[0];
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...(rows || [])].sort((a, b) => {
      const av = a[col.key];
      const bv = b[col.key];
      if (col.type === 'string') {
        return (av || '').localeCompare(bv || '', undefined, { sensitivity: 'base' }) * dir;
      }
      // Push nulls to the bottom regardless of direction.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return ((av ?? 0) - (bv ?? 0)) * dir;
    });
  }, [rows, columns, sortKey, sortDir]);

  const visible = pageSize === 'all' || pageSize == null ? sorted : sorted.slice(0, pageSize);

  // Hand the enclosing ChartCard the whole sorted set, so its share button can
  // export every row rather than the page that happens to be on screen.
  const tableExport = useContext(ChartTableExport);
  const wrapRef = useRef(null);
  useEffect(() => {
    const node = wrapRef.current;
    if (!tableExport || !node) return;
    tableExport.register(node, { columns, rows: sorted });
    return () => tableExport.unregister(node);
  }, [tableExport, columns, sorted]);

  if (!rows || rows.length === 0) {
    // Still carries the ref, so the card knows it holds a table and its share
    // button exports a header-only CSV instead of failing to find a chart.
    return (
      <div ref={wrapRef} style={{ padding: '20px 0', color: 'var(--text-light)', fontSize: 13, textAlign: 'center' }}>
        {emptyText || 'No data for this range.'}
      </div>
    );
  }

  return (
    <div ref={wrapRef}>
      <div className="scroll-x leaderboard-scroll" style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', fontSize: 13 }}>
          <thead>
            <tr>
              {expandable && <th style={{ width: 24, padding: '6px 4px' }}></th>}
              <th style={{ textAlign: 'right', width: 36, color: 'var(--text-secondary)', padding: '6px 8px' }}>#</th>
              {columns.map(col => (
                <th key={col.key}
                    onClick={() => toggleSort(col)}
                    style={{ textAlign: col.align, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', padding: '6px 8px' }}>
                  {col.label}{' '}
                  {sortKey === col.key ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row, i) => {
              const key = rowKey ? rowKey(row, i) : i;
              // `expandable` accepts a boolean OR a per-row predicate so we can
              // hide the caret on rows that have nothing to expand into.
              const rowExpandable = typeof expandable === 'function' ? expandable(row) : !!expandable;
              const isExpanded = rowExpandable && expandedKeys.has(key);
              return (
                <React.Fragment key={key}>
                  <tr onClick={onRowClick ? () => onRowClick(row) : (rowExpandable ? () => toggleExpanded(key) : undefined)}
                      style={(onRowClick || rowExpandable) ? { cursor: 'pointer' } : undefined}>
                    {expandable && (
                      <td style={{ width: 24, padding: '6px 4px', textAlign: 'center', color: 'var(--text-secondary)', userSelect: 'none' }}>
                        {rowExpandable ? (isExpanded ? '▾' : '▸') : ''}
                      </td>
                    )}
                    <td style={{ textAlign: 'right', color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums', padding: '6px 8px' }}>{i + 1}</td>
                    {columns.map(col => (
                      <td key={col.key} style={{ textAlign: col.align, padding: '6px 8px' }}>
                        {col.render ? col.render(row) : (col.fmt ? col.fmt(row[col.key]) : (row[col.key] ?? '--'))}
                      </td>
                    ))}
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={columns.length + 1 + (expandable ? 1 : 0)} style={{ padding: '8px 12px 12px 40px', background: 'var(--bg, #fafafa)' }}>
                        {renderExpanded ? renderExpanded(row) : null}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Horizontal BarChart for the leaderboard panels.
function LeaderboardChart({ data, dataKey, color, valueFormatter, metricLabel }) {
  if (!data || data.length === 0) {
    return <div style={{ padding: '20px 0', color: 'var(--text-light)', fontSize: 13, textAlign: 'center' }}>No data for this range.</div>;
  }
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={valueFormatter} />
        <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={160}
          tickFormatter={(v) => truncate(v, 24)} />
        <Tooltip content={<LeaderboardTooltip metricLabel={metricLabel} valueFormatter={valueFormatter} />} />
        <Bar dataKey={dataKey} fill={color} radius={[0, 3, 3, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// Tooltip for the Team Market Hit Rate chart — the rate on its own hides how
// much evidence is behind it, so the raw hits/breaks always ride along.
function TeamMarketTooltip({ active, payload }) {
  if (!active || !payload || payload.length === 0) return null;
  const m = payload[0].payload;
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', boxShadow: 'var(--shadow)', fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{m.label}</div>
      <div>Hit rate: <strong>{(m.hit_rate_pct ?? 0).toFixed(1)}%</strong></div>
      <div>Hit in <strong>{m.hits}</strong> of <strong>{m.breaks}</strong> break{m.breaks === 1 ? '' : 's'}</div>
    </div>
  );
}

// How often each team market hits, one bar per market. Horizontal because the
// labels are three nicknames long and there are up to 33 of them; the card
// grows with the row count rather than squeezing them into a fixed height.
function TeamMarketHitRateChart({ rows }) {
  if (!rows || rows.length === 0) {
    return <div style={{ padding: '20px 0', color: 'var(--text-light)', fontSize: 13, textAlign: 'center' }}>No completed breaks in this range.</div>;
  }
  return (
    <ResponsiveContainer width="100%" height={Math.max(180, rows.length * 22 + 32)}>
      <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 44, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
        <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={200}
          tickFormatter={(v) => truncate(v, 34)} interval={0} />
        <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} content={<TeamMarketTooltip />} />
        <Bar dataKey="hit_rate_pct" fill="#3b82f6" radius={[0, 3, 3, 0]}>
          <LabelList dataKey="hit_rate_pct" position="right"
            style={{ fontSize: 10, fill: 'var(--text-secondary)' }}
            formatter={(v) => `${(v ?? 0).toFixed(0)}%`} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// Break Cycle Anatomy — where the time in a break cycle actually goes.
//
// Part-to-whole, so a horizontal stacked bar: the four segments compose one
// cycle, and their proportions are the point. Horizontal because the segment
// names are long.
//
// Colour encodes the only distinction that matters here — productive time vs
// dead time — rather than four arbitrary identities. Blue family for the two
// segments that produce something (selling, ripping), warm family for the two
// that produce nothing. Both families were run through the palette validator
// against the light chart surface: worst adjacent CVD ΔE 17.7 (protan), well
// clear of the 8 target, all four inside the lightness band, above the chroma
// floor and over 3:1 contrast.
//
// If a dark theme ever lands, these are the validated dark-surface steps:
// fill #3987e5, fill→rip #d95926, rip #256abf, between #e66767.
const CYCLE_SEGMENTS = [
  { key: 'fill', label: 'Fill', color: '#3987e5', dead: false,
    desc: 'First spot sold to last spot sold.' },
  { key: 'fill_to_rip', label: 'Fill → Rip', color: '#eb6834', dead: true,
    desc: 'Sold out, but nothing opened yet — last spot sold to the first card marked.' },
  { key: 'rip', label: 'Rip & Recap', color: '#1c5cab', dead: false,
    desc: 'First card marked to End Break, recap included.' },
  { key: 'between_breaks', label: 'Between Breaks', color: '#d03b3b', dead: true,
    desc: 'This break ending to the next one starting.' },
];

// A segment narrower than this can't hold its own label without the text
// colliding with the gaps on either side, so the legend carries it instead.
const MIN_LABEL_SHARE = 12;

function BreakCycleChart({ timing }) {
  const [hovered, setHovered] = useState(null);

  const segs = useMemo(() => CYCLE_SEGMENTS.map(s => ({
    ...s,
    ...(timing?.segments?.[s.key] || { avg_seconds: null, measured: 0 }),
  })), [timing]);

  const cycle = timing?.cycle_seconds || 0;
  const plotted = segs.filter(s => s.avg_seconds != null && s.avg_seconds > 0);

  if (!timing || cycle <= 0 || plotted.length === 0) {
    return (
      <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-light)', fontSize: 13 }}>
        No measurable break timing in this range yet — the segments fill in as breaks are
        ended with the End Break button.
      </div>
    );
  }

  const share = (s) => (s.avg_seconds / cycle) * 100;
  const partial = timing.segments_present < CYCLE_SEGMENTS.length;
  // Empty segments that do have data, just not in this window.
  const missingWithData = segs.filter(s => s.measured === 0 && s.latest_recorded);
  const elsewhere = missingWithData.length;
  const latestElsewhere = missingWithData
    .map(s => s.latest_recorded)
    .sort()
    .at(-1);

  return (
    <div>
      {/* Hero: the cycle, and how much of it produced nothing. The dead figure
          wears the dead-time hue so the bar below reads as its breakdown. */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <span style={{ fontSize: 30, fontWeight: 700, lineHeight: 1, color: 'var(--text)' }}>
          {formatDuration(cycle)}
        </span>
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>average cycle</span>
        {timing.dead_seconds > 0 && (
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            · <strong style={{ color: '#d03b3b' }}>{formatDuration(timing.dead_seconds)} dead</strong>
            {timing.dead_pct != null ? ` (${timing.dead_pct.toFixed(0)}%)` : ''}
          </span>
        )}
      </div>

      {/* The bar. 2px surface-coloured gaps do the separating — no strokes —
          and only the outer ends are rounded, so the stack reads as one bar. */}
      <div style={{ position: 'relative' }}>
        <div style={{ display: 'flex', height: 24, width: '100%' }}>
          {plotted.map((s, i) => {
            const pct = share(s);
            const first = i === 0;
            const last = i === plotted.length - 1;
            const dim = hovered && hovered !== s.key;
            return (
              <div
                key={s.key}
                onMouseEnter={() => setHovered(s.key)}
                onMouseLeave={() => setHovered(null)}
                title={`${s.label}: ${formatDuration(s.avg_seconds)}`}
                style={{
                  width: `${pct}%`,
                  background: s.color,
                  marginRight: last ? 0 : 2,
                  borderRadius: `${first ? 4 : 0}px ${last ? 4 : 0}px ${last ? 4 : 0}px ${first ? 4 : 0}px`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'default',
                  opacity: dim ? 0.55 : 1,
                  transition: 'opacity 0.12s',
                  minWidth: 2,
                }}
              >
                {/* Labelled only where the text genuinely fits — a clipped
                    label is worse than none, and the legend has every value. */}
                {pct >= MIN_LABEL_SHARE && (
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
                    {formatDuration(s.avg_seconds)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend — always present, and the table view: every segment's value and
          the number of breaks behind it, so a two-break average can't pass for
          a two-hundred-break one. */}
      <div style={{
        // Four columns at full width so the legend sits one-per-segment under
        // the bar, collapsing to two rather than leaving a single orphan.
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '12px 16px', marginTop: 16,
      }}>
        {segs.map(s => (
          <div key={s.key}
            onMouseEnter={() => setHovered(s.key)}
            onMouseLeave={() => setHovered(null)}
            // minWidth 0 so a long label can't push the grid track wider than
            // its share and force the whole card to overflow on a phone.
            style={{ minWidth: 0, opacity: hovered && hovered !== s.key ? 0.55 : 1, transition: 'opacity 0.12s' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{
                width: 10, height: 10, borderRadius: 2, background: s.color, flexShrink: 0,
              }} />
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{s.label}</span>
              {s.dead && (
                <span style={{
                  fontSize: 9, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
                  color: '#9c2b2b', background: 'rgba(208,59,59,0.12)', padding: '1px 5px', borderRadius: 3,
                }}>dead</span>
              )}
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, marginTop: 2, color: 'var(--text)' }}>
              {s.avg_seconds != null ? formatDuration(s.avg_seconds) : '--'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-light)' }}>
              {s.measured > 0
                ? `${s.measured} break${s.measured === 1 ? '' : 's'} measured`
                // Distinguish "this range has none" from "this is never
                // captured". The stock ranges end yesterday, so a range can sit
                // one day short of the only data there is.
                : s.latest_recorded
                  ? `none in this range · last on ${formatDate(s.latest_recorded)}`
                  : 'not recorded yet'}
            </div>
          </div>
        ))}
      </div>

      {partial && (
        <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-light)' }}>
          The cycle above sums only the {timing.segments_present} segment
          {timing.segments_present === 1 ? '' : 's'} with data, so it understates a full cycle
          until every segment has been measured.
          {elsewhere && (
            <> The missing {elsewhere === 1 ? 'one was' : 'ones were'} recorded outside this
            date range — widen it to include {formatDate(latestElsewhere)}.</>
          )}
        </div>
      )}
    </div>
  );
}

export default function Insights() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const canSeeFinancials = !!user && ['admin', 'partner'].includes(user.role);
  const [previewBreakId, setPreviewBreakId] = useState(null);

  const [data, setData] = useState(null);
  const [breaksPerf, setBreaksPerf] = useState(null);
  const [dateRange, setDateRange] = useState(getDatePreset('last_30d'));
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState('avgGmv'); // avgGmv, avgOrders, gmv, orders
  const [selectedCell, setSelectedCell] = useState(null);

  const [layout, setLayout] = useState(DEFAULT_LAYOUT);

  // Inline rename of non-template break-composition labels.
  const [editingCompHash, setEditingCompHash] = useState(null);
  const [compEditValue, setCompEditValue] = useState('');
  const [compSaving, setCompSaving] = useState(false);

  // Per-table page size (10/20/50/100/All), default 10.
  const [compositionsPageSize, setCompositionsPageSize] = useState(10);
  const [breaksPageSize, setBreaksPageSize] = useState(10);
  // Spot Price Curve: the average shape of a fill across the range's
  // sold-out breaks, each on its own price scale.
  const [spotPriceCurve, setSpotPriceCurve] = useState(null);
  useEffect(() => {
    if (!canSeeFinancials || !dateRange?.startDate || !dateRange?.endDate) return;
    setSpotPriceCurve(null);
    api.get('/api/insights/spot-price-curve', { params: dateRange })
      .then(({ data }) => setSpotPriceCurve(data))
      .catch(() => setSpotPriceCurve({ breaks_included: 0, buckets: [] }));
  }, [dateRange, canSeeFinancials]);
  const [productsPageSize, setProductsPageSize] = useState(10);
  // Minimum # Breaks filter for the Break Compositions table.
  const [minBreaks, setMinBreaks] = useState(1);
  // Break Compositions figures: 'avg' = per break, 'total' = summed over the range.
  const [compositionsMode, setCompositionsMode] = useState('avg');
  // Live Days chart state: 'week' | 'month' | 'all'
  const [liveDaysGroupBy, setLiveDaysGroupBy] = useState('week');
  const [liveDays, setLiveDays] = useState(null);
  // Team Market Hit Rate chart: server response + the minimum-sample filter.
  const [marketHitRate, setMarketHitRate] = useState(null);
  const [marketMinBreaks, setMarketMinBreaks] = useState(1);
  // Break cycle timing — its own round trip, like the market hit rate, because
  // the between-breaks gap is pairwise and needs the whole ordered set.
  const [breakTiming, setBreakTiming] = useState(null);

  // Re-fetch the Live Days breakdown whenever the date range or grouping flips.
  useEffect(() => {
    if (!canSeeFinancials || !dateRange?.startDate || !dateRange?.endDate) return;
    api.get('/api/insights/live-days', { params: { ...dateRange, groupBy: liveDaysGroupBy } })
      .then(({ data }) => setLiveDays(data))
      .catch(() => setLiveDays(null));
  }, [dateRange, liveDaysGroupBy, canSeeFinancials]);

  // Team market hit rates. Its own round trip rather than a field on
  // breaks-performance: that endpoint counts live and pending breaks too, and
  // only a completed break is guaranteed to have its hits keyed in.
  useEffect(() => {
    if (!canSeeFinancials || !dateRange?.startDate || !dateRange?.endDate) return;
    api.get('/api/insights/team-market-hit-rate', { params: dateRange })
      .then(({ data }) => setMarketHitRate(data))
      .catch(() => setMarketHitRate(null));
  }, [dateRange, canSeeFinancials]);

  useEffect(() => {
    if (!canSeeFinancials || !dateRange?.startDate || !dateRange?.endDate) return;
    api.get('/api/insights/break-timing', { params: dateRange })
      .then(({ data }) => setBreakTiming(data))
      .catch(() => setBreakTiming(null));
  }, [dateRange, canSeeFinancials]);

  // Format a YYYY-MM-DD week start or YYYY-MM month into a short readable label.
  const liveDaysData = useMemo(() => {
    if (!liveDays || liveDays.groupBy === 'all') return [];
    return (liveDays.buckets || []).map(b => {
      let label = b.bucket;
      if (liveDays.groupBy === 'week') {
        const d = new Date(b.bucket + 'T12:00:00');
        label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      } else if (liveDays.groupBy === 'month') {
        const [y, m] = b.bucket.split('-');
        const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
        label = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      }
      return { label, days: b.days };
    });
  }, [liveDays]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const perfPromise = api.get('/api/insights/performance', { params: dateRange });
      const breaksPromise = canSeeFinancials
        ? api.get('/api/insights/breaks-performance', { params: dateRange }).catch(() => null)
        : Promise.resolve(null);
      const [perfRes, breaksRes] = await Promise.all([perfPromise, breaksPromise]);
      setData(perfRes.data);
      setBreaksPerf(breaksRes?.data || null);
    } catch (err) { console.error('Insights error:', err); }
    setLoading(false);
  }, [dateRange, canSeeFinancials]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Load saved layout once on mount.
  useEffect(() => {
    api.get('/api/insights/layout').then(({ data: d }) => {
      setLayout(mergeWithDefaults(d?.layout));
    }).catch(() => { /* fall back to defaults */ });
  }, []);

  const metricLabel = metric === 'avgGmv' ? 'Avg GMV' : metric === 'avgOrders' ? 'Avg Orders' : metric === 'gmv' ? 'Total GMV' : 'Total Orders';
  const isCurrency = metric.includes('Gmv') || metric === 'gmv';
  const fmtVal = (v) => isCurrency ? formatCurrency(v) : Math.round(v).toLocaleString();
  const fmtAxis = (v) => isCurrency ? (v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${Math.round(v)}`) : Math.round(v).toString();

  const bestHour = data?.byHour?.reduce((best, h) => h[metric] > (best?.[metric] || 0) ? h : best, null);
  const bestDay = data?.byDay?.reduce((best, d) => d[metric] > (best?.[metric] || 0) ? d : best, null);

  // Each break gets a derived date_time_iso so the Date & Time column sorts chronologically.
  const leaderboards = useMemo(() => {
    if (!breaksPerf) return null;
    return {
      compositions: breaksPerf.compositions || [],
      breaks: (breaksPerf.breaks || []).map(b => ({
        ...b,
        date_time_iso: b.date ? `${b.date}T${b.scheduled_time || '00:00'}` : '',
      })),
      products: breaksPerf.products || [],
    };
  }, [breaksPerf]);

  // The header now shows each table's row count, so the rows have to be
  // resolved (and, for compositions, filtered) before the card renders.
  //
  // Both modes are projected onto the same column keys — `net_sales` is either
  // the average or the total depending on the mode — so flipping the toggle
  // keeps whatever column the table is sorted by. Only the ranking changes,
  // which is the point: the composition with the best average is often not the
  // one that earned the most over the period.
  const compositionRows = useMemo(() => {
    const total = compositionsMode === 'total';
    return (leaderboards?.compositions || [])
      .filter(c => (c.mix_count || 0) >= minBreaks)
      .map(c => ({
        hash: c.hash,
        label: c.label,
        label_source: c.label_source,
        mix_count: c.mix_count,
        // Per break this is revenue per spot sold; totalled it is how many
        // spots the composition sold across the range.
        spot: total ? c.total_spots : c.avg_spot,
        net_sales: total ? c.total_net_sales : c.avg_net_sales,
        cogs: total ? c.total_cogs : c.avg_cogs,
        net_profit: total ? c.total_net_profit : c.avg_net_profit,
        margin_pct: total ? c.total_margin_pct : c.avg_margin_pct,
      }));
  }, [leaderboards, minBreaks, compositionsMode]);
  const breakRows = leaderboards?.breaks || [];
  const productRows = leaderboards?.products || [];

  // Already sorted by rate on the server; the filter drops thin samples, where
  // one break in the range is enough to read as a flat 0% or 100%.
  const marketRows = useMemo(
    () => (marketHitRate?.markets || []).filter(m => (m.breaks || 0) >= marketMinBreaks),
    [marketHitRate, marketMinBreaks]
  );

  function startCompEdit(c) {
    setEditingCompHash(c.hash);
    setCompEditValue(c.label || '');
  }
  function cancelCompEdit() {
    setEditingCompHash(null);
    setCompEditValue('');
  }
  function renderBreakTypeCell(c) {
    if (editingCompHash === c.hash) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            autoFocus
            value={compEditValue}
            onChange={(e) => setCompEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveCompEdit(c.hash);
              else if (e.key === 'Escape') cancelCompEdit();
            }}
            disabled={compSaving}
            style={{ flex: 1, minWidth: 160, padding: '4px 8px', fontSize: 13 }}
          />
          <button className="btn btn-primary btn-sm" disabled={compSaving} onClick={() => saveCompEdit(c.hash)}>Save</button>
          <button className="btn btn-secondary btn-sm" disabled={compSaving} onClick={cancelCompEdit}>Cancel</button>
        </div>
      );
    }
    const editable = c.label_source !== 'template';
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span>{c.label}</span>
        {editable && (
          <button
            onClick={() => startCompEdit(c)}
            title="Rename break type"
            aria-label="Rename break type"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: 2, color: 'var(--text-light)', display: 'inline-flex',
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--primary)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-light)'}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
          </button>
        )}
      </span>
    );
  }
  async function saveCompEdit(hash) {
    if (compSaving) return;
    setCompSaving(true);
    try {
      await api.put(`/api/insights/compositions/${encodeURIComponent(hash)}/label`, { label: compEditValue });
      setEditingCompHash(null);
      setCompEditValue('');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save name');
    } finally {
      setCompSaving(false);
    }
  }

  const currencyAxis = (v) => v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${Math.round(v)}`;
  const pctAxis = (v) => `${Math.round(v)}%`;

  // Render helpers per chart — keyed by chart ID.
  function renderChart(id) {
    if (id === 'peak-callouts') {
      return (
        <div className="peak-callouts" style={{ display: 'flex', gap: 12, alignItems: 'stretch', flexWrap: 'wrap', marginBottom: 12 }}>
          <div className="peak-callout" style={{ flex: 1, minWidth: 200, padding: '14px 18px', background: 'var(--bg-card)', borderRadius: 8, boxShadow: 'var(--shadow)', borderLeft: '4px solid #22c55e' }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Best Hour</div>
            <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4 }}>{bestHour?.label || '--'}</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
              {bestHour ? `${fmtVal(bestHour[metric])} ${metricLabel.toLowerCase()}` : ' '}
            </div>
          </div>
          <div className="peak-callout" style={{ flex: 1, minWidth: 200, padding: '14px 18px', background: 'var(--bg-card)', borderRadius: 8, boxShadow: 'var(--shadow)', borderLeft: '4px solid #3b82f6' }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Best Day</div>
            <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4 }}>{bestDay?.label || '--'}</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
              {bestDay ? `${fmtVal(bestDay[metric])} ${metricLabel.toLowerCase()}` : ' '}
            </div>
          </div>
        </div>
      );
    }

    if (id === 'by-hour') {
      return (
        <ChartCard title={`${metricLabel} by Hour`}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data?.byHour || []}>
              <CartesianGrid strokeDasharray="3 3" />
              {/* 24 hourly categories. Every other label already collides at
                phone width, so thin them further — the bars and the tooltip
                still carry every hour. */}
            <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={isMobile ? 3 : 1} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
              <Tooltip formatter={(v) => fmtVal(v)} labelFormatter={(l) => `Hour: ${l}`} />
              <Bar dataKey={metric} name={metricLabel} fill="#22c55e" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      );
    }

    if (id === 'by-day') {
      return (
        <ChartCard title={`${metricLabel} by Day of Week`}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data?.byDay || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="shortLabel" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
              <Tooltip formatter={(v) => fmtVal(v)} labelFormatter={(l) => {
                const d = data?.byDay?.find(d => d.shortLabel === l);
                return d ? `${d.label} (${d.dayCount} days)` : l;
              }} />
              <Bar dataKey={metric} name={metricLabel} fill="#3b82f6" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      );
    }

    // compositions-table, breaks-table, and products-table are rendered outside
    // the grid in JSX below.


    if (id === 'heatmap') {
      if (!data) return null;
      const max = Math.max(...(data.heatmap || []).flatMap(r => r.hours.map(h => h[metric] || 0)));
      return (
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="card-title" style={{ marginBottom: 12 }}>
            Day &times; Hour Heatmap
            <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-light)', marginLeft: 8 }}>
              ({metricLabel} &mdash; click a cell for details)
            </span>
          </div>
          <div className="scroll-x sticky-col heatmap-scroll" style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'separate', borderSpacing: '2px', width: '100%' }}>
              <thead>
                <tr>
                  <th></th>
                  {HOUR_LABELS.map((l, i) => (
                    <th key={i} style={{ fontSize: 10, fontWeight: 500, padding: '4px 2px', textAlign: 'center', color: 'var(--text-light)' }}>{l}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.heatmap.map(row => (
                  <tr key={row.dow}>
                    <td style={{ fontSize: 12, fontWeight: 600, padding: '4px 8px', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{row.shortLabel}</td>
                    {row.hours.map((cell, h) => (
                      <HeatmapCell
                        key={h}
                        value={cell[metric] || 0}
                        max={max}
                        selected={selectedCell?.dow === row.dow && selectedCell?.hour === h}
                        onClick={() => setSelectedCell(selectedCell?.dow === row.dow && selectedCell?.hour === h ? null : {
                          dow: row.dow, hour: h, dayLabel: row.label,
                          hourLabel: data.byHour[h]?.label,
                          ...cell,
                        })}
                      />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {selectedCell && (
            <div style={{ marginTop: 12, padding: '12px 16px', background: 'var(--bg)', borderRadius: 8, fontSize: 13 }}>
              <strong>{selectedCell.dayLabel} at {selectedCell.hourLabel}</strong>
              <div style={{ display: 'flex', gap: 24, marginTop: 6, flexWrap: 'wrap' }}>
                <span>Total GMV: <strong>{formatCurrency(selectedCell.gmv)}</strong></span>
                <span>Total Orders: <strong>{selectedCell.orders}</strong></span>
                <span>Avg GMV/Day: <strong>{formatCurrency(selectedCell.avgGmv)}</strong></span>
                <span>Avg Orders/Day: <strong>{Math.round(selectedCell.avgOrders * 10) / 10}</strong></span>
              </div>
            </div>
          )}
        </div>
      );
    }

    return null;
  }

  const gridLayout = layout.filter(it => CHART_IDS.includes(it.i));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <h2 className="page-title" style={{ margin: 0 }}>Performance Insights</h2>
      </div>

      <DateRangeFilter onChange={setDateRange} initial="last_30d" />

      {loading && <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}><div className="spinner" /></div>}

      {!loading && data && (
        <>
          <div className="insights-metrics" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Metric:</span>
            {[
              { key: 'avgGmv', label: 'Avg GMV/Day' },
              { key: 'avgOrders', label: 'Avg Orders/Day' },
              { key: 'gmv', label: 'Total GMV' },
              { key: 'orders', label: 'Total Orders' },
            ].map(m => (
              <button key={m.key} className={`date-preset ${metric === m.key ? 'active' : ''}`}
                onClick={() => setMetric(m.key)} style={{ padding: '4px 12px', fontSize: 12 }}>
                {m.label}
              </button>
            ))}
          </div>

          {renderChart('peak-callouts')}

          {/* On a phone the grid is the problem rather than the solution.
              react-grid-layout positions every item absolutely at a fixed
              rowHeight and clips its overflow, and only an `lg` layout is
              supplied — so at narrow widths it reuses desktop heights for
              content that has reflowed taller. Stack the charts at their
              natural height instead. */}
          {isMobile ? (
            <div className="insights-stack">
              {gridLayout.map(item => (
                <div key={item.i}>{renderChart(item.i)}</div>
              ))}
            </div>
          ) : (
          <ResponsiveGridLayout
            className="insights-grid"
            layouts={{ lg: gridLayout }}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
            cols={{ lg: 12, md: 12, sm: 6, xs: 4 }}
            rowHeight={70}
            isDraggable={false}
            isResizable={false}
            margin={[12, 12]}
          >
            {gridLayout.map(item => (
              <div key={item.i} style={{ overflow: 'hidden' }}>
                {renderChart(item.i)}
              </div>
            ))}
          </ResponsiveGridLayout>
          )}

          {canSeeFinancials && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12, marginBottom: 12 }}>
              <ChartCard
                title="Live Days"
                controls={[
                  { key: 'week',  label: 'Weekly' },
                  { key: 'month', label: 'Monthly' },
                  { key: 'all',   label: 'All' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setLiveDaysGroupBy(opt.key)}
                    className={`date-preset ${liveDaysGroupBy === opt.key ? 'active' : ''}`}
                    style={{ padding: '4px 12px', fontSize: 12 }}
                  >
                    {opt.label}
                  </button>
                ))}
              >
                {liveDaysGroupBy === 'all' ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240 }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 64, fontWeight: 700, color: '#3b82f6', lineHeight: 1 }}>
                        {liveDays?.total ?? '—'}
                      </div>
                      <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-secondary)' }}>
                        live day{liveDays?.total === 1 ? '' : 's'} in selected range
                      </div>
                    </div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={liveDaysData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip formatter={(v) => `${v} live day${v === 1 ? '' : 's'}`} />
                      <Bar dataKey="days" name="Live Days" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
              <ChartCard
                title="Break Compositions"
                controls={
                  <>
                    <div className="chart-toggle">
                      {[
                        { key: 'avg',   label: 'Avg' },
                        { key: 'total', label: 'Total' },
                      ].map(opt => (
                        <button
                          key={opt.key}
                          onClick={() => setCompositionsMode(opt.key)}
                          className={`date-preset ${compositionsMode === opt.key ? 'active' : ''}`}
                          style={{ padding: '4px 12px', fontSize: 12 }}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    <label>
                      Min # Breaks:
                      <input
                        type="number"
                        min={1}
                        value={minBreaks}
                        onChange={e => {
                          const n = parseInt(e.target.value, 10);
                          setMinBreaks(Number.isFinite(n) && n >= 1 ? n : 1);
                        }}
                        style={{ width: 56 }}
                      />
                    </label>
                    <TableControls total={compositionRows.length} pageSize={compositionsPageSize} onPageSizeChange={setCompositionsPageSize} />
                  </>
                }
                note={compositionsMode === 'total'
                  ? 'Totals across every run of this composition in the selected range.'
                  : undefined}
              >
                <LeaderboardTable
                  rows={compositionRows}
                  columns={compositionsMode === 'total' ? [
                    { key: 'label',      label: 'Break Type', align: 'left',  type: 'string', render: (c) => renderBreakTypeCell(c) },
                    { key: 'mix_count',  label: '# Breaks',   align: 'right', type: 'number' },
                    { key: 'spot',       label: 'Spots Sold', align: 'right', type: 'number', fmt: (v) => v == null ? '--' : v.toLocaleString() },
                    { key: 'net_sales',  label: 'Revenue',    align: 'right', type: 'number', fmt: formatCurrency },
                    { key: 'cogs',       label: 'COGS',       align: 'right', type: 'number', fmt: formatCurrency },
                    { key: 'net_profit', label: 'Profit',     align: 'right', type: 'number', fmt: formatCurrency },
                    { key: 'margin_pct', label: 'Margin',     align: 'right', type: 'number', fmt: (v) => `${(v ?? 0).toFixed(1)}%` },
                  ] : [
                    { key: 'label',      label: 'Break Type',  align: 'left',  type: 'string', render: (c) => renderBreakTypeCell(c) },
                    { key: 'mix_count',  label: '# Breaks',    align: 'right', type: 'number' },
                    { key: 'spot',       label: 'Avg Spot',    align: 'right', type: 'number', fmt: (v) => v == null ? '--' : formatCurrency(v) },
                    { key: 'net_sales',  label: 'Avg Revenue', align: 'right', type: 'number', fmt: formatCurrency },
                    { key: 'cogs',       label: 'Avg COGS',    align: 'right', type: 'number', fmt: formatCurrency },
                    { key: 'net_profit', label: 'Avg Profit',  align: 'right', type: 'number', fmt: formatCurrency },
                    { key: 'margin_pct', label: 'Avg Margin',  align: 'right', type: 'number', fmt: (v) => `${(v ?? 0).toFixed(1)}%` },
                  ]}
                  defaultSortKey="net_sales"
                  defaultSortDir="desc"
                  rowKey={(c) => c.hash}
                  pageSize={compositionsPageSize}
                />
              </ChartCard>

              <ChartCard
                title="Top Breaks"
                controls={<TableControls total={breakRows.length} pageSize={breaksPageSize} onPageSizeChange={setBreaksPageSize} />}
              >
                <LeaderboardTable
                  rows={breakRows}
                  columns={[
                    { key: 'name',          label: 'Break Name', align: 'left',  type: 'string',
                      render: (b) => `#${b.break_number ?? '--'} ${b.name || ''}`.trim(),
                      csv: (b) => `#${b.break_number ?? '--'} ${b.name || ''}`.trim() },
                    { key: 'date_time_iso', label: 'Date & Time', align: 'left',  type: 'string',
                      render: (b) => formatBreakDateTime(b) },
                    { key: 'avg_spot',      label: 'Avg Spot',    align: 'right', type: 'number', fmt: (v) => v == null ? '--' : formatCurrency(v) },
                    { key: 'net_sales',     label: 'Revenue',     align: 'right', type: 'number', fmt: formatCurrency },
                    { key: 'cogs',          label: 'COGS',        align: 'right', type: 'number', fmt: formatCurrency },
                    { key: 'net_profit',    label: 'Profit',      align: 'right', type: 'number', fmt: formatCurrency },
                    { key: 'margin_pct',    label: 'Margin',      align: 'right', type: 'number', fmt: (v) => `${(v ?? 0).toFixed(1)}%` },
                    { key: 'final_score',   label: 'Score',       align: 'right', type: 'number',
                      render: (b) => b.final_score == null ? '--' : (
                        <span style={{
                          display: 'inline-block', minWidth: 30, padding: '2px 8px', borderRadius: 4,
                          background: scoreBg(b.final_score), color: scoreColor(b.final_score),
                          fontWeight: 600, fontSize: 12, fontVariantNumeric: 'tabular-nums',
                        }}>{b.final_score}</span>
                      ) },
                  ]}
                  defaultSortKey="net_sales"
                  defaultSortDir="desc"
                  rowKey={(b) => b.id}
                  pageSize={breaksPageSize}
                  onRowClick={(b) => setPreviewBreakId(b.id)}
                />
              </ChartCard>

              <ChartCard
                title="Spot Price Curve"
                note="How spot prices move as a board fills, averaged across every sold-out break in range. Each sale is shown as a % of its own break's average spot price, so a $200 break and a $30 break count equally, and the fill is split into ten steps so a 32-spot and an 8-spot break line up. Each break counts once per step. Resales of traded-in teams are ordinary sales here, and trade-in totals are left out. Click a break in Top Breaks for its own timeline."
              >
                <SpotPriceCurve data={spotPriceCurve} />
              </ChartCard>

              <ChartCard
                title="Team Market Hit Rate"
                controls={
                  <>
                    <label>
                      Min # Breaks:
                      <input
                        type="number"
                        min={1}
                        value={marketMinBreaks}
                        onChange={e => {
                          const n = parseInt(e.target.value, 10);
                          setMarketMinBreaks(Number.isFinite(n) && n >= 1 ? n : 1);
                        }}
                        style={{ width: 56 }}
                      />
                    </label>
                    <span className="chart-toggle">
                      {marketRows.length} market{marketRows.length === 1 ? '' : 's'}
                    </span>
                  </>
                }
                note={`Share of the completed breaks that put a market in play where its spot recorded a hit, out of ${marketHitRate?.total_breaks ?? 0} completed break${marketHitRate?.total_breaks === 1 ? '' : 's'} in range. Each market's denominator is the completed breaks whose category carries it, so a market absent from a category (no NBA team, or Miscellaneous outside Multi-Sport) counts only the breaks it was actually in. Single-sport spots roll up to the same market, so a Football break's Giants spot counts toward Giants / Knicks / Yankees. Division breaks are excluded — a division sells as one spot, so its hit can't be traced to a single market.`}
              >
                <TeamMarketHitRateChart rows={marketRows} />
              </ChartCard>

              <ChartCard
                title="Break Cycle Anatomy"
                note={`Where the time goes in an average break cycle: selling the board out (Fill), waiting to start (Fill → Rip), opening and recapping (Rip & Recap), then the gap to the next break (Between Breaks). Fill starts when the first spot is filled and ends when the last one sells. The rip starts at the first card marked in Card(s) after the board is full — a card marked earlier does not count, since nothing is ripped until every spot is sold — and ends when the breaker presses End Break, recap included. Between Breaks only counts breaks that run directly into one another: a gap over ${Math.round((breakTiming?.contiguous_gap_seconds ?? 1800) / 60)} minutes is treated as a separate session rather than dead time. Each segment averages only the breaks with a trustworthy pair of stamps, and reports how many that was. Per-break, not per-LIVE — one TikTok LIVE usually contains several breaks.`}
              >
                <BreakCycleChart timing={breakTiming} />
              </ChartCard>

              <ChartCard
                title="Top Products"
                controls={<TableControls total={productRows.length} pageSize={productsPageSize} onPageSizeChange={setProductsPageSize} />}
              >
                <LeaderboardTable
                  rows={productRows}
                  columns={[
                    { key: 'product_name', label: 'Product Name',  align: 'left',  type: 'string' },
                    { key: 'units_sold',   label: 'Sold',          align: 'right', type: 'number' },
                    { key: 'net_sales',    label: 'Net Sales',     align: 'right', type: 'number', fmt: formatCurrency },
                    { key: 'net_profit',   label: 'Net Profit',    align: 'right', type: 'number', fmt: formatCurrency },
                    { key: 'margin_pct',   label: 'Profit Margin', align: 'right', type: 'number', fmt: (v) => `${(v ?? 0).toFixed(1)}%` },
                  ]}
                  defaultSortKey="net_sales"
                  defaultSortDir="desc"
                  rowKey={(p) => p.product_id}
                  pageSize={productsPageSize}
                  expandable={(p) => (p.skus?.length || 0) > 1}
                  renderExpanded={(parent) => {
                    const skus = parent?.skus || [];
                    if (skus.length === 0) {
                      return <div style={{ fontSize: 12, color: 'var(--text-light)' }}>No SKU breakdown available.</div>;
                    }
                    const skuColumns = [
                      { key: 'sku_label',  label: 'SKU',           align: 'left',  type: 'string' },
                      { key: 'units_sold', label: 'Sold',          align: 'right', type: 'number' },
                      { key: 'net_sales',  label: 'Net Sales',     align: 'right', type: 'number', fmt: formatCurrency },
                      { key: 'net_profit', label: 'Net Profit',    align: 'right', type: 'number', fmt: formatCurrency },
                      { key: 'margin_pct', label: 'Profit Margin', align: 'right', type: 'number', fmt: (v) => `${(v ?? 0).toFixed(1)}%` },
                    ];
                    return (
                      <table style={{ width: '100%', fontSize: 12 }}>
                        <thead>
                          <tr style={{ color: 'var(--text-secondary)' }}>
                            {skuColumns.map(c => (
                              <th key={c.key} style={{ textAlign: c.align, padding: '4px 8px', fontWeight: 600 }}>{c.label}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {skus.map(s => (
                            <tr key={s.sku_id}>
                              {skuColumns.map(c => (
                                <td key={c.key} style={{ textAlign: c.align, padding: '4px 8px' }}>
                                  {c.fmt ? c.fmt(s[c.key]) : (s[c.key] ?? '--')}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  }}
                />
              </ChartCard>
            </div>
          )}

          {/* Financial-only, and in flow: it is a table, so its height is its
              content's. */}
          {canSeeFinancials && renderChart('heatmap')}
        </>
      )}

      {previewBreakId && (
        <BreakDetailPanel
          breakId={previewBreakId}
          canSeeFinancials={canSeeFinancials}
          onClose={() => setPreviewBreakId(null)}
        />
      )}
    </div>
  );
}
